import { Injectable, inject } from '@angular/core';
import type {
  AddressAutocompleteItem,
  AddressAutocompleteRequest,
} from '../models/address-autocomplete';
import type { AddressRetrieveDetail } from '../models/address-retrieve';
import type { ValidateAddressRequest, ValidateAddressResponse } from '../models/address-validate';
import { AnalyticsService, trackTimedApiCall } from '../integrations/analytics';
import { ShipmentFlowStateService } from '../services/shipment-flow-state.service';
import { AXIOS_INSTANCE } from './api-client.tokens';

export interface AddressAutocompleteRequestOptions {
  signal?: AbortSignal;
}

@Injectable({ providedIn: 'root' })
export class AddressValidationApiService {
  private readonly http = inject(AXIOS_INSTANCE);
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly analytics = inject(AnalyticsService);

  /**
   * Requests street address autocomplete suggestions from address-validation-service.
   */
  autocomplete(
    request: AddressAutocompleteRequest,
    options: AddressAutocompleteRequestOptions = {},
  ): Promise<AddressAutocompleteItem[]> {
    const params = new URLSearchParams({
      searchTerm: request.searchTerm,
      country: request.country,
      languagePreference: request.languagePreference ?? 'en',
    });

    if (request.maxItems !== undefined) {
      params.set('maxItems', String(request.maxItems));
    }

    if (request.lastId) {
      params.set('lastId', request.lastId);
    }

    return this.http
      .get<AddressAutocompleteItem[]>(`/addresses/autocomplete?${params.toString()}`, {
        signal: options.signal,
      })
      .then((response) => response.data);
  }

  /**
   * Retrieves structured address details for a selected autocomplete suggestion id.
   */
  retrieve(id: string): Promise<AddressRetrieveDetail[]> {
    const params = new URLSearchParams({ id });

    return this.http
      .get<AddressRetrieveDetail[]>(`/addresses/retrieve?${params.toString()}`)
      .then((response) => response.data);
  }

  /**
   * Validates one or more shipment addresses via Canada Post AVS.
   * Sends the wizard flow id so the Lambda can mint a JWT attestation.
   */
  validate(request: ValidateAddressRequest): Promise<ValidateAddressResponse> {
    const flowId = this.shipmentFlowState.ensureFlowId();

    return trackTimedApiCall(this.analytics, 'callAVS', () =>
      this.http
        .post<ValidateAddressResponse>('/addresses/validate', request, {
          headers: { 'x-flow-id': flowId },
        })
        .then((response) => response.data),
    );
  }
}
